"use client";

import { useMemo } from "react";
import { format } from "date-fns";
import { Clock, Trash2, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import type { TimeRange, BookedTimeRange } from "@/lib/calendar-types";
import { CalendarIcon, Cross2Icon } from "@radix-ui/react-icons";

interface SelectionPanelProps {
  selectedRanges: TimeRange[];
  bookedRanges: BookedTimeRange[];
  onRemoveRange: (rangeId: string) => void;
  onClearAll: () => void;
}

export default function SelectionPanel({
  selectedRanges,
  bookedRanges,
  onRemoveRange,
  onClearAll,
}: SelectionPanelProps) {
  // Group time ranges by day
  const groupedSelectedRanges = useMemo(() => {
    const groups: Record<string, TimeRange[]> = {};

    // Sort ranges by start date
    const sortedRanges = [...selectedRanges].sort((a, b) => {
      // Ensure dates are Date objects
      const aStartDate =
        a.startDate instanceof Date ? a.startDate : new Date(a.startDate);
      const bStartDate =
        b.startDate instanceof Date ? b.startDate : new Date(b.startDate);
      return aStartDate.getTime() - bStartDate.getTime();
    });

    sortedRanges.forEach((range) => {
      // Ensure date is a Date object
      const startDate =
        range.startDate instanceof Date
          ? range.startDate
          : new Date(range.startDate);
      const day = format(startDate, "yyyy-MM-dd");
      if (!groups[day]) {
        groups[day] = [];
      }
      groups[day].push(range);
    });

    return groups;
  }, [selectedRanges]);

  // Group booked ranges by day
  const groupedBookedRanges = useMemo(() => {
    const groups: Record<string, BookedTimeRange[]> = {};

    // Sort ranges by start date
    const sortedRanges = [...bookedRanges].sort((a, b) => {
      // Ensure dates are Date objects
      const aStartDate =
        a.startDate instanceof Date ? a.startDate : new Date(a.startDate);
      const bStartDate =
        b.startDate instanceof Date ? b.startDate : new Date(b.startDate);
      return aStartDate.getTime() - bStartDate.getTime();
    });

    sortedRanges.forEach((range) => {
      // Ensure date is a Date object
      const startDate =
        range.startDate instanceof Date
          ? range.startDate
          : new Date(range.startDate);
      const day = format(startDate, "yyyy-MM-dd");
      if (!groups[day]) {
        groups[day] = [];
      }
      groups[day].push(range);
    });

    return groups;
  }, [bookedRanges]);

  // Format date for display
  const formatDay = (dateString: string) => {
    const date = new Date(dateString);
    return format(date, "EEEE, MMMM d, yyyy");
  };

  // Update the formatTimeRange function to include duration and handle non-Date objects
  const formatTimeRange = (
    startDate: Date | string | number,
    endDate: Date | string | number,
  ) => {
    // Ensure startDate and endDate are Date objects
    const startDateObj =
      startDate instanceof Date ? startDate : new Date(startDate);
    const endDateObj = endDate instanceof Date ? endDate : new Date(endDate);

    // Check if valid dates were created
    if (isNaN(startDateObj.getTime()) || isNaN(endDateObj.getTime())) {
      console.error("Invalid date encountered:", { startDate, endDate });
      return (
        <div>
          <div className="font-medium text-sm text-red-500">
            Invalid date range
          </div>
        </div>
      );
    }

    const durationMinutes = Math.round(
      (endDateObj.getTime() - startDateObj.getTime()) / (1000 * 60),
    );
    const hours = Math.floor(durationMinutes / 60);
    const minutes = durationMinutes % 60;

    let durationText = "";
    if (hours > 0) {
      durationText += `${hours}h`;
    }
    if (minutes > 0 || hours === 0) {
      durationText += `${minutes}m`;
    }

    return (
      <div>
        <div className="font-medium text-sm">
          {format(startDateObj, "h:mm a")} - {format(endDateObj, "h:mm a")}
        </div>
        <div className="text-xs text-muted-foreground mt-0.5">
          Duration: {durationText}
        </div>
      </div>
    );
  };

  return (
    <Card className="h-[600px] flex flex-col">
      <CardHeader className="pb-2">
        <CardTitle>Slot Segments</CardTitle>
        <CardDescription>View and manage slot segments</CardDescription>
      </CardHeader>

      <Tabs defaultValue="selected" className="flex-1 flex flex-col">
        <div className="px-6">
          <TabsList className="w-full">
            <TabsTrigger value="selected" className="flex-1">
              Segments
              {selectedRanges.length > 0 && (
                <Badge variant="secondary" className="ml-2">
                  {selectedRanges.length}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="booked" className="flex-1">
              Booked
              {bookedRanges.length > 0 && (
                <Badge variant="secondary" className="ml-2">
                  {bookedRanges.length}
                </Badge>
              )}
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent
          value="selected"
          className="flex-1 overflow-hidden mt-0 pt-2"
        >
          <CardContent className="p-0 flex-1 overflow-hidden">
            {selectedRanges.length === 0 ? (
              <Alert className="mx-6">
                <AlertDescription className="flex flex-col items-center justify-center py-8 text-center">
                  <CalendarIcon className="h-12 w-12 text-muted-foreground mb-4" />
                  <p>No slot segments selected.</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Click and drag across the calendar to select slot segments.
                  </p>
                </AlertDescription>
              </Alert>
            ) : (
              <ScrollArea className="h-[calc(100%-40px)] px-6">
                <div className="space-y-6">
                  {Object.entries(groupedSelectedRanges).map(
                    ([day, ranges]) => (
                      <div key={day} className="space-y-2">
                        <h3 className="font-medium text-sm">
                          {formatDay(day)}
                        </h3>
                        <div className="space-y-2">
                          {ranges.map((range) => (
                            <Card
                              key={range.id}
                              className="border border-muted"
                            >
                              <CardContent className="p-3">
                                <div className="flex items-center justify-between">
                                  <div className="flex items-start gap-2">
                                    <Clock className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                                    {formatTimeRange(
                                      range.startDate,
                                      range.endDate,
                                    )}
                                  </div>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-6 w-6 text-muted-foreground hover:text-destructive"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      // Prevent multiple clicks
                                      const currentTarget = e.currentTarget;
                                      currentTarget.disabled = true;
                                      onRemoveRange(range.id);
                                      // Re-enable after a short delay
                                      setTimeout(() => {
                                        currentTarget.disabled = false;
                                      }, 300);
                                    }}
                                  >
                                    <Cross2Icon className="h-4 w-4" />
                                    <span className="sr-only">Remove</span>
                                  </Button>
                                </div>
                              </CardContent>
                            </Card>
                          ))}
                        </div>
                      </div>
                    ),
                  )}
                </div>
              </ScrollArea>
            )}
          </CardContent>
          <CardFooter className="border-t pt-4">
            <Button
              variant="outline"
              className="w-full"
              onClick={onClearAll}
              disabled={selectedRanges.length === 0}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Clear All Segments
            </Button>
          </CardFooter>
        </TabsContent>

        <TabsContent
          value="booked"
          className="flex-1 overflow-hidden mt-0 pt-2"
        >
          <CardContent className="p-0 flex-1 overflow-hidden">
            {bookedRanges.length === 0 ? (
              <Alert className="mx-6">
                <AlertDescription className="flex flex-col items-center justify-center py-8 text-center">
                  <Lock className="h-12 w-12 text-muted-foreground mb-4" />
                  <p>No booked time slots.</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Booked slots will appear here.
                  </p>
                </AlertDescription>
              </Alert>
            ) : (
              <ScrollArea className="h-[calc(100%-40px)] px-6">
                <div className="space-y-6">
                  {Object.entries(groupedBookedRanges).map(([day, ranges]) => (
                    <div key={day} className="space-y-2">
                      <h3 className="font-medium text-sm">{formatDay(day)}</h3>
                      <div className="space-y-2">
                        {ranges.map((range) => (
                          <Card
                            key={range.id}
                            className="border border-red-200"
                          >
                            <CardContent className="p-3">
                              <div className="flex items-start gap-2">
                                <Lock className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
                                <div>
                                  <div className="font-medium text-sm">
                                    {range.title}
                                  </div>
                                  <div className="text-xs">
                                    {format(
                                      range.startDate instanceof Date
                                        ? range.startDate
                                        : new Date(range.startDate),
                                      "h:mm a",
                                    )}{" "}
                                    -{" "}
                                    {format(
                                      range.endDate instanceof Date
                                        ? range.endDate
                                        : new Date(range.endDate),
                                      "h:mm a",
                                    )}
                                  </div>
                                  {range.bookedBy && (
                                    <div className="text-xs text-muted-foreground/80 mt-1">
                                      Booked by: {range.bookedBy}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            )}
          </CardContent>
        </TabsContent>
      </Tabs>
    </Card>
  );
}
